import { useEffect, useRef, useState } from "react";
import {
  caretToDisplay,
  displayValue,
  enterCaretMode,
  fieldFrom,
  focusField,
  pressBackspace,
  pressClear,
  pressDigit,
  pressDot,
  type FieldState,
} from "../convert/amountField";
import { amountValue, swapBase, typedAmount, type AmountState } from "../convert/baseSwap";
import {
  convert,
  formatAmount,
  formatUnitRate,
  hasRate,
  newestDate,
  oldestDate,
  rateDate,
} from "../convert/convert";
/*
 * parseAmount is no longer called from here: the keypad emits clean Latin
 * digits, so there is nothing to normalise on a keystroke. It remains the PASTE
 * guard — Arabic-Indic digits, both separator conventions, leading currency
 * symbols and trailing junk still arrive that way — and keeps its full test
 * coverage. It gets wired back in when paste handling lands.
 */
import { currencyMeta, currencyName, currencyNameCounted } from "../currencies/meta";
import { formatNumber, type AppLocale } from "../i18n/format";
import { moveItem, useRowGestures } from "../interaction/useRowGestures";
import { dataAge } from "../rates/age";
import { deriveCoverage } from "../rates/coverage";
import { coverageCopy, coverageLabel } from "../rates/coverageCopy";
import type { RateSnapshot } from "../rates/fixture";
import { CurrencyRow } from "./CurrencyRow";
import { DataAgeIndicator } from "./DataAgeIndicator";
import { Keypad } from "./Keypad";
import { RowSheet } from "./RowSheet";
import { CurrencyPicker } from "./CurrencyPicker";
import { InfoIcon, PlusIcon } from "./Icons";

/** Announcements are throttled so typing does not flood the live region. */
const ANNOUNCE_THROTTLE_MS = 500;

interface Props {
  readonly locale: AppLocale;
  readonly snapshot: RateSnapshot;
  readonly base: string;
  /** Ordered list of every currency on screen, base included. */
  readonly codes: readonly string[];
  readonly amount: AmountState;
  readonly onBaseChange: (base: string, amount: AmountState) => void;
  readonly onCodesChange: (codes: readonly string[]) => void;
  readonly onAmountChange: (amount: AmountState) => void;
  /** Injectable so the age gauge is deterministic in tests. */
  readonly now?: Date;
  readonly offline?: boolean;
}

export function ConvertScreen({
  locale,
  snapshot,
  base,
  codes,
  amount,
  onBaseChange,
  onCodesChange,
  onAmountChange,
  now = new Date(),
  offline = false,
}: Props) {
  const ar = locale === "ar";

  // Only currencies we can actually price. A row with no rate would have to
  // show a blank or a guess, and neither is acceptable in a converter.
  const priced = codes.filter((code) => hasRate(snapshot, code));

  /*
   * THE BASE ROW KEEPS ITS PLACE IN THE LIST.
   *
   * This used to render the base first and the rest after it, which pinned it
   * to position zero. That silently breaks drag-to-reorder before it is even
   * built: move a row, then change the base, and the row leaves the position
   * you put it in. Order belongs to the user; being the base is a STATE, shown
   * by the row's treatment, not by where it sits.
   *
   * The base is appended only if it is somehow absent from the list, so the
   * editable field can never disappear.
   */
  const visible = priced.includes(base) ? priced : [...priced, base];
  const quotes = visible.filter((code) => code !== base);

  /*
   * Conversion happens here, synchronously, during render. No debounce and no
   * effect: every keystroke recomputes the whole column before paint. Debouncing
   * the maths is what makes a converter feel laggy, and the work is a handful of
   * decimal divisions — far cheaper than the render it would be deferring.
   */
  const value = amountValue(amount);

  const freshest = newestDate(snapshot, visible);
  const age = dataAge(oldestDate(snapshot, visible), now, offline);
  const baseName = currencyName(base, locale);

  /* ---- polite, throttled announcement of the converted total ---- */
  const [announcement, setAnnouncement] = useState("");
  const pending = useRef("");
  const lastAnnouncedAt = useRef(0);

  const firstQuote = quotes[0];
  const summary =
    firstQuote === undefined
      ? ""
      : ar
        ? `${amount.input || "0"} ${baseName} تساوي ${formatAmount(
            convert(value, base, firstQuote, snapshot),
            firstQuote,
          )} ${currencyName(firstQuote, locale)}`
        : `${amount.input || "0"} ${baseName} equals ${formatAmount(
            convert(value, base, firstQuote, snapshot),
            firstQuote,
          )} ${currencyName(firstQuote, locale)}`;

  useEffect(() => {
    pending.current = summary;
    const wait = Math.max(0, ANNOUNCE_THROTTLE_MS - (Date.now() - lastAnnouncedAt.current));
    const timer = setTimeout(() => {
      lastAnnouncedAt.current = Date.now();
      setAnnouncement(pending.current);
    }, wait);
    return () => clearTimeout(timer);
  }, [summary]);

  /* ---- the amount field ---- */
  /*
   * The field state machine is local; the parent still owns the canonical
   * amount. `lastEmitted` keeps the two in step without a loop: an edit here
   * pushes up and records what it pushed, so the resync below only fires when
   * the amount changed for some OTHER reason — a base swap, a restored
   * session — and would otherwise silently reset the user's caret mid-typing.
   */
  const [field, setField] = useState<FieldState>(() => fieldFrom(amount.input));
  const [keypadOpen, setKeypadOpen] = useState(false);
  const lastEmitted = useRef(amount.input);

  useEffect(() => {
    if (amount.input !== lastEmitted.current) {
      lastEmitted.current = amount.input;
      setField(fieldFrom(amount.input));
    }
  }, [amount.input]);

  const commit = (next: FieldState) => {
    setField(next);
    if (next.raw !== lastEmitted.current) {
      lastEmitted.current = next.raw;
      onAmountChange(typedAmount(next.raw));
    }
  };

  const selection = keypadOpen
    ? field.selectAll
      ? { start: 0, end: displayValue(field.raw).length }
      : {
          start: caretToDisplay(field.raw, field.caret),
          end: caretToDisplay(field.raw, field.caret),
        }
    : null;

  const handleFieldTap = () => {
    setKeypadOpen(true);
    // First tap selects everything; a tap while already selected drops into
    // caret editing for the rare person who wants to change one digit.
    setField((prev) => (prev.selectAll ? enterCaretMode(prev) : focusField(prev)));
  };

  /* ---- reorder, remove, undo ---- */

  const [removed, setRemoved] = useState<{ code: string; index: number } | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [gestureNote, setGestureNote] = useState("");
  /** Which row's coverage sentence is expanded. One at a time. */
  const [openNote, setOpenNote] = useState<string | null>(null);
  /** Which row's three-dot sheet is open. Null when none is. */
  const [sheetCode, setSheetCode] = useState<string | null>(null);
  /** The picker is open when true. One instance, wherever it was opened from. */
  const [pickerOpen, setPickerOpen] = useState(false);
  /** A row asked for by the picker, scrolled to and briefly marked. */
  const [revealed, setRevealed] = useState<string | null>(null);

  const reorder = (from: number, to: number) => {
    const moved = visible[from];
    const target = visible[to];
    if (moved === undefined || target === undefined || from === to) return;
    onCodesChange(moveItem(codes, codes.indexOf(moved), codes.indexOf(target)));
    setGestureNote(
      ar
        ? `نُقل ${currencyName(moved, locale)} إلى الموضع ${formatNumber(to + 1, locale)}`
        : `${currencyName(moved, locale)} moved to position ${formatNumber(to + 1, locale)}`,
    );
  };

  const remove = (code: string) => {
    const index = codes.indexOf(code);
    if (index === -1) return;
    if (undoTimer.current !== null) clearTimeout(undoTimer.current);
    onCodesChange(codes.filter((c) => c !== code));
    setRemoved({ code, index });
    // Five seconds: long enough to notice, short enough that the toast is not
    // still sitting there once attention has moved on.
    undoTimer.current = setTimeout(() => setRemoved(null), 5000);
  };

  const undoRemove = () => {
    if (!removed) return;
    if (undoTimer.current !== null) clearTimeout(undoTimer.current);
    const next = [...codes];
    next.splice(Math.min(removed.index, next.length), 0, removed.code);
    onCodesChange(next);
    setRemoved(null);
  };

  const gestures = useRowGestures({
    codes: visible,
    rtl: ar,
    onReorder: reorder,
    onRemove: remove,
    // Tapping a quote row makes it the base. The base row is already the base,
    // so its own tap does nothing rather than re-running a no-op swap.
    onTap: (code) => {
      if (code !== base) handleMakeBase(code);
    },
  });

  /**
   * Keyboard reordering — a requirement, not an afterthought. Someone who
   * cannot drag must still be able to order their list. Alt+Arrow rather than a
   * bare arrow, because bare arrows belong to moving BETWEEN rows.
   */
  const onRowKeyDown = (code: string) => (event: React.KeyboardEvent) => {
    if (!event.altKey) return;
    const delta = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const from = visible.indexOf(code);
    reorder(from, Math.max(0, Math.min(visible.length - 1, from + delta)));
  };

  const handleMakeBase = (code: string) => {
    const result = swapBase(amount, base, code, snapshot);
    onBaseChange(result.base, result.amount);
  };

  const unitRateFor = (code: string) => {
    const rate = formatUnitRate(snapshot, base, code);
    /*
     * Counted forms on both sides of the sentence:
     *   en  "1 Saudi Riyal equals 0.2667 US Dollars"   (plural)
     *   ar  "1 ريال سعودي يساوي 0.2667 دولار أمريكي"    (no definite article)
     * Using the plain names here would produce "1 الريال السعودي", which is
     * simply ungrammatical.
     */
    const quoteName = currencyNameCounted(code, locale);
    // The base side is always exactly 1, so English stays singular there and
    // only the quote side pluralises. Arabic drops the article on both.
    const countedBase = ar ? currencyNameCounted(base, locale) : baseName;
    return {
      text: `1 ${base} = ${rate} ${code}`,
      accessible: ar
        ? `1 ${countedBase} يساوي ${rate} ${quoteName}`
        : `1 ${countedBase} equals ${rate} ${quoteName}`,
    };
  };

  /** A row is flagged stale only when it lags the freshest row on screen. */
  const staleDateFor = (code: string): string | null => {
    const own = rateDate(snapshot, code);
    if (own === null || freshest === null) return null;
    return own < freshest ? own : null;
  };

  /*
   * Coverage is derived per render from what the providers returned, never
   * read from a stored verdict. A currency that becomes withheld between
   * sessions therefore explains itself on the next open rather than showing a
   * figure we no longer stand behind.
   */
  const coverageOf = (code: string) => {
    const entry = snapshot.rates[code];
    const meta = currencyMeta(code);
    return deriveCoverage(entry?.observations ?? [], {
      now,
      ...(meta.pegged ? { peg: { to: "USD" as const, rate: meta.pegged.rate } } : {}),
    });
  };

  const renderRow = (code: string, isBase: boolean) => {
    const { text, accessible } = unitRateFor(code);
    const coverage = coverageOf(code);
    const note = coverageCopy(coverage, locale);
    return (
      <CurrencyRow
        key={code}
        code={code}
        locale={locale}
        isBase={isBase}
        amountInput={displayValue(field.raw)}
        amountDisplay={formatAmount(convert(value, base, code, snapshot), code)}
        unitRateText={text}
        unitRateAccessible={accessible}
        staleDate={staleDateFor(code)}
        coverageState={coverage.state}
        coverageNote={note?.text ?? null}
        coverageLabel={coverageLabel(coverage, locale)}
        noteOpen={openNote === code}
        onToggleNote={() => setOpenNote((prev) => (prev === code ? null : code))}
        selection={isBase ? selection : null}
        dragging={gestures.state.draggingCode === code}
        dragOffset={gestures.state.draggingCode === code ? gestures.state.dy : 0}
        neighbourShift={
          gestures.state.draggingCode === code ? 0 : gestures.state.shiftFor(visible.indexOf(code))
        }
        swipeProgress={gestures.state.swipingCode === code ? gestures.state.swipeProgress : 0}
        willRemove={gestures.state.swipingCode === code && gestures.state.willRemove}
        gestureProps={gestures.bind(code)}
        revealed={revealed === code}
        onRowKeyDown={onRowKeyDown(code)}
        onFieldTap={handleFieldTap}
        onMakeBase={() => handleMakeBase(code)}
        onAction={() => (isBase ? setKeypadOpen(true) : setSheetCode(code))}
        onPickCurrency={() => setPickerOpen(true)}
      />
    );
  };

  /*
   * The pad has to be a SIBLING of the screen, not a child of it, or it would
   * scroll away with the rows instead of pushing them. The flag lives on <html>
   * for the same reason the appearance attributes do: chrome outside this
   * component — the nav bar — needs to react to it without prop drilling.
   */
  /*
   * Scrolls to a row the picker was asked about and marks it briefly.
   *
   * Tapping an already-selected currency asks "where is it?", so the answer has
   * to be visual and has to survive a list long enough to scroll. The mark
   * clears itself; nothing waits on it, and the row is interactive the whole
   * time — motion accompanies the answer rather than gating it.
   */
  useEffect(() => {
    if (revealed === null) return;
    const row = document.querySelector<HTMLElement>(`[data-code="${revealed}"]`);
    row?.scrollIntoView({ block: "center", behavior: "smooth" });
    const timer = setTimeout(() => setRevealed(null), 1600);
    return () => clearTimeout(timer);
  }, [revealed]);

  useEffect(() => {
    const root = document.documentElement;
    if (keypadOpen) root.dataset.keypad = "open";
    else delete root.dataset.keypad;
    return () => {
      delete document.documentElement.dataset.keypad;
    };
  }, [keypadOpen]);

  return (
    <>
    <main className="screen">
      <header className="screen__status">
        <DataAgeIndicator age={age} locale={locale} />
      </header>

      <table className="rate-table" role="table">
        <caption className="visually-hidden">
          {ar
            ? `تحويل العملات من ${baseName}`
            : `Currency conversions from ${baseName}`}
        </caption>

        <thead className="visually-hidden" role="rowgroup">
          <tr role="row">
            <th role="columnheader" scope="col">
              {ar ? "العملة" : "Currency"}
            </th>
            <th role="columnheader" scope="col">
              {ar ? "المبلغ" : "Amount"}
            </th>
            <th role="columnheader" scope="col">
              {ar ? "إجراءات" : "Actions"}
            </th>
          </tr>
        </thead>

        <tbody className="rate-table__body" role="rowgroup">
          {visible.map((code) => renderRow(code, code === base))}
        </tbody>
      </table>

      {/*
       * The empty area below the rows becomes the add-currency target rather
       * than staying blank with a small link in the corner. It grows to fill
       * whatever space is left, so a short list reads as an invitation instead
       * of an unfinished screen — and the space is filled by the one action a
       * user with a short list actually wants.
       */}
      <button
        type="button"
        className="add-target"
        onClick={() => setPickerOpen(true)}
      >
        <PlusIcon />
        <span>{ar ? "إضافة عملة" : "Add currency"}</span>
      </button>

      <div className="screen__controls">
        <button
          type="button"
          className="control control--info"
          onClick={() => console.log("[stub] rate info")}
        >
          <span>{ar ? "أسعار السوق المتوسطة" : "Mid-market rates"}</span>
          <InfoIcon />
        </button>
      </div>

      {snapshot.isFixture ? (
        <p className="screen__fixture-note" role="note">
          {ar
            ? "بيانات تجريبية مؤقتة — لم يُوصل محرّك الأسعار الحيّ بعد."
            : "Temporary fixture data — the live rate engine is not wired yet."}
        </p>
      ) : null}

      {removed ? (
        <div className="toast" role="status">
          <span>
            {ar
              ? `أُزيل ${currencyName(removed.code, locale)}`
              : `${currencyName(removed.code, locale)} removed`}
          </span>
          <button type="button" className="toast__undo" onClick={undoRemove}>
            {ar ? "تراجع" : "Undo"}
          </button>
        </div>
      ) : null}

      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      {/* Reordering is silent to a screen reader without this. */}
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {gestureNote}
      </div>

      <p className="visually-hidden">
        {ar
          ? `عدد العملات المعروضة ${formatNumber(visible.length, locale)}`
          : `${formatNumber(visible.length, locale)} currencies shown`}
      </p>
    </main>

      {keypadOpen ? (
        <Keypad
          locale={locale}
          canRestore={field.raw === "" && field.cleared !== null}
          onDigit={(d) => commit(pressDigit(field, d))}
          onDot={() => commit(pressDot(field))}
          onBackspace={() => commit(pressBackspace(field))}
          onClear={() => commit(pressClear(field))}
          onDone={() => setKeypadOpen(false)}
        />
      ) : null}

      {/*
       * One sheet for whichever row asked for it, not one per row. Rendering a
       * sheet inside every row would mount six focus traps and six scroll locks
       * and leave which of them "wins" to the DOM.
       */}
      {sheetCode !== null ? (
        <RowSheet
          open
          onClose={() => setSheetCode(null)}
          locale={locale}
          code={sheetCode}
          unitRateText={unitRateFor(sheetCode).text}
          asOf={rateDate(snapshot, sheetCode)}
          source={snapshot.source}
          coverage={coverageOf(sheetCode)}
          onMakeBase={() => handleMakeBase(sheetCode)}
          onRemove={() => remove(sheetCode)}
        />
      ) : null}

      <CurrencyPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        locale={locale}
        selected={visible}
        coverageFor={coverageOf}
        onAdd={(code) => {
          // Appended, never inserted: the order is the user's, and a picker has
          // no business deciding where in their list a new row belongs.
          if (!codes.includes(code)) onCodesChange([...codes, code]);
          setRevealed(code);
        }}
        onReveal={setRevealed}
      />
    </>
  );
}
